import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { clearCredentials } from "./auth.slice";
import type { RootState } from "./index";

export interface PermissionRole {
    roleId: string;
    roleName: string;
}

export interface PermissionsState {
    allowedActions: string[];
    roles: PermissionRole[];
    /** Server-decided flag: whether the user may access the app at all (BFF access manifest). */
    canAccessApp: boolean;
}

const initialState: PermissionsState = {
    allowedActions: [],
    roles: [],
    canAccessApp: false,
};

function clearPermissionsReducer(state: PermissionsState): void {
    state.allowedActions = [];
    state.roles = [];
    state.canAccessApp = false;
}

export const permissionsSlice = createSlice({
    name: "permissions",
    initialState,
    reducers: {
        setPermissions(state, action: PayloadAction<PermissionsState>) {
            state.allowedActions = action.payload.allowedActions;
            state.roles = action.payload.roles;
            state.canAccessApp = action.payload.canAccessApp;
        },
        clearPermissions: clearPermissionsReducer,
    },
    extraReducers(builder) {
        // Mirror logout so permissions are cleared whenever auth is cleared.
        builder.addCase(clearCredentials, clearPermissionsReducer);
    },
});

export const { setPermissions, clearPermissions } = permissionsSlice.actions;

export const selectPermissions = (state: RootState) => state.permissions;
export const selectAllowedActions = (state: RootState) => state.permissions.allowedActions;
export const selectCanAccessApp = (state: RootState) => state.permissions.canAccessApp;
